import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useAuth from '../context/AuthProvider';
import { StateInformationPortalUrl } from '../Constants';
import { AppRoutes } from '../helper/appRoutes';

const FolderPlusIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M56 16H32L26 8H8C5.79086 8 4 9.79086 4 12V52C4 54.2091 5.79086 56 8 56H56C58.2091 56 60 54.2091 60 52V20C60 17.7909 58.2091 16 56 16Z" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M32 28V44" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M24 36H40" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DocumentIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M40 8H16C13.7909 8 12 9.79086 12 12V52C12 54.2091 13.7909 56 16 56H48C50.2091 56 52 54.2091 52 52V20L40 8Z" stroke="#616161" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M40 8V20H52" stroke="#616161" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 28H44" stroke="#616161" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 36H44" stroke="#616161" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 44H32" stroke="#616161" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DollarIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 8V66" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M44 20H26C23.8783 20 21.8434 20.8429 20.3431 22.3431C18.8429 23.8434 18 25.8783 18 28C18 30.1217 18.8429 32.1566 20.3431 33.6569C21.8434 35.1571 23.8783 36 26 36H38C40.1217 36 42.1566 36.8429 43.6569 38.3431C45.1571 39.8434 46 41.8783 46 44C46 46.1217 45.1571 48.1566 43.6569 49.6569C42.1566 51.1571 40.1217 52 38 52H18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CursorIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 12L28 52L34 36L50 30L12 12Z" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M34 36L50 52" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="24" stroke="#8b4513" strokeWidth="2.5" fill="none"/>
    <path d="M32 44V32" stroke="#8b4513" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="32" cy="22" r="2" fill="#8b4513"/>
  </svg>
);

const ArrowCircleIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="24" stroke="#8b4513" strokeWidth="2.5" fill="none"/>
    <path d="M24 32H40" stroke="#8b4513" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M34 26L40 32L34 38" stroke="#8b4513" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface DashboardTileProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  onClick: () => void;
  iconBgColor?: string;
}

const DashboardTile: React.FC<DashboardTileProps> = ({ icon, title, subtitle, onClick, iconBgColor = '#e3f2fd' }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        minHeight: '200px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
        },
      }}
    >
      <Box
        sx={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: iconBgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          color: '#424242',
          fontSize: '20px',
          fontWeight: 700,
          textAlign: 'center',
          fontFamily: "'Proxima Nova', sans-serif",
          mb: subtitle ? 1 : 0,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            color: '#757575',
            fontSize: '14px',
            fontWeight: 400,
            textAlign: 'center',
            fontFamily: "'Proxima Nova', sans-serif",
            lineHeight: 1.4,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

export const AdminHomePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleFilingQueueClick = () => {
      navigate(`${AppRoutes.root}8`);
  };

  const handleStateReportsClick = () => {
      navigate(AppRoutes.reporting.stateReport);
  };

  const handleReconciliationQueueClick = () => {
    navigate(`${AppRoutes.reconciliationQueue}/0`);
  };

  const handleIWantToClick = () => {
    navigate(AppRoutes.home.iWantTo);
  };

  const handleStateInfoPortalClick = () => {
    window.open(StateInformationPortalUrl, '_blank');
  };

  const handleNotificationsClick = () => {
    navigate(AppRoutes.home.base);
  };

  return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#f5f5f5',
          padding: '24px',
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            maxWidth: '1200px',
            margin: '0 auto',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              mb: 4,
              mt: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#424242',
                fontFamily: "'Proxima Nova', sans-serif",
                mb: 1,
              }}
            >
              Hello, {user?.userFirstName || 'User'}!
            </Typography>
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 400,
                color: '#757575',
                fontFamily: "'Proxima Nova', sans-serif",
                opacity: 0.9,
              }}
            >
              Let's Get Started
            </Typography>
          </Box>
          <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <DashboardTile
              icon={<FolderPlusIcon />}
              title="Filing Queue"
              onClick={handleFilingQueueClick}
              iconBgColor="#e3f2fd"
            />
          </Grid>
          {(user?.userRoles?.includes('MISC State Reporter') || user?.userRoles?.includes('Super Admin')) && (
            <Grid item xs={12} sm={6} md={4}>
              <DashboardTile
                icon={<DocumentIcon />}
                title="State Reports"
                onClick={handleStateReportsClick}
                iconBgColor="#f5f5f5"
              />
            </Grid>
          )}
          {(user?.userRoles?.includes('MISC State Reporter') || user?.userRoles?.includes('Super Admin')) && (
            <Grid item xs={12} sm={6} md={4}>
              <DashboardTile
                icon={<DollarIcon />}
                title="Reconciliation Queue"
                onClick={handleReconciliationQueueClick}
                iconBgColor="#e3f2fd"
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4}>
            <DashboardTile
              icon={<CursorIcon />}
              title="I Want To..."
              onClick={handleIWantToClick}
              iconBgColor="#e3f2fd"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DashboardTile
              icon={<InfoIcon />}
              title="Go to State Information Portal"
              onClick={handleStateInfoPortalClick}
              iconBgColor="#fff3e0"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DashboardTile
              icon={<ArrowCircleIcon />}
              title="My Notifications"
              onClick={handleNotificationsClick}
              iconBgColor="#fff3e0"
            />
          </Grid>
        </Grid>
        </Box>
      </Box>
  );
};

export default AdminHomePage;